/**
 * Auto-reload balance configuration.
 */

"use client";

import { useState, useEffect } from "react";
import { Button } from "@speakai/ui/button";
import { Input } from "@speakai/ui/input";
import { Card } from "@speakai/ui/card";
import { Switch } from "@speakai/ui/switch";
import { Tooltip } from "@speakai/ui/tooltip";
import { useTranslation } from "react-i18next";
import { useToast } from "@speakai/ui/toast";
import { track } from "@/lib/integrations/sentry-tracking";
import { useAutoReload } from "../payment-queries";
import { useUpdateAutoReload } from "../mutations";

const AMOUNT_PRESETS = [25, 50, 100, 200, 500];
const THRESHOLD_PRESETS = [1, 2, 5, 8, 10];
const NO_PERMISSION_MSG = "Contact your account admin to manage auto-reload settings.";

interface AutoReloadProps {
  canManageBilling?: boolean;
  /** When true, render the form without its own Card wrapper (it lives inside a Dialog). */
  inDialog?: boolean;
}

export function AutoReload({ canManageBilling = true, inDialog = false }: AutoReloadProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const { data: config, isLoading, isError, refetch } = useAutoReload();
  const updateAutoReload = useUpdateAutoReload();
  const unit = "USD";

  const [isEnabled, setIsEnabled] = useState(false);
  const [amount, setAmount] = useState(50);
  const [threshold, setThreshold] = useState(10);
  const [hasChanges, setHasChanges] = useState(false);

  // Coalesce undefined → literal default to keep inputs controlled across the
  // load transition (React forbids changing uncontrolled → controlled once mounted).
  useEffect(() => {
    if (config) {
      setIsEnabled(config.isActive ?? false);
      setAmount(config.reloadAmount ?? 50);
      setThreshold(config.minAmount ?? 10);
    }
  }, [config]);

  function handleToggle() {
    setIsEnabled((prev) => !prev);
    setHasChanges(true);
  }

  function handleSave() {
    updateAutoReload.mutate(
      { isActive: isEnabled, reloadAmount: amount, minAmount: threshold },
      {
        onSuccess: () => {
          setHasChanges(false);
          track("payment", "auto_reload");
          toast.success(t("PAYMENT_SETTINGS.RELOAD.SAVE_SUCCESS"));
        },
        onError: () => toast.error(t("PAYMENT_SETTINGS.RELOAD.SAVE_ERROR")),
      }
    );
  }

  const loadingBody = (
    <div className="space-y-3">
      <div className="h-6 w-48 animate-pulse rounded bg-muted" />
      <div className="h-10 animate-pulse rounded bg-muted" />
      <div className="h-10 animate-pulse rounded bg-muted" />
    </div>
  );
  if (isLoading) return inDialog ? loadingBody : <Card className="p-6">{loadingBody}</Card>;

  const errorBody = (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-danger" role="alert">
        {t("PAYMENT_SETTINGS.RELOAD.LOAD_ERROR", "Failed to load auto-reload settings.")}
      </p>
      <Button variant="outline" size="sm" onClick={() => refetch()} className="w-fit">
        {t("ACTION.RETRY", "Retry")}
      </Button>
    </div>
  );
  if (isError) return inDialog ? errorBody : <Card className="p-6">{errorBody}</Card>;

  const body = (
    <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-1">
          {!inDialog && (
            <h3 className="text-lg font-semibold">{t("PAYMENT_SETTINGS.RELOAD.TITLE")}</h3>
          )}
          <p className="text-sm text-muted-foreground">
            {t("PAYMENT_SETTINGS.RELOAD.DESC")}
          </p>
        </div>

        <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-muted/30 px-4 py-3">
          <div>
            <p className="text-sm font-medium">
              {isEnabled ? t("ACTION.ENABLED", "Enabled") : t("ACTION.DISABLED", "Disabled")}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("PAYMENT_SETTINGS.RELOAD.RELOAD_STATUS", "Auto-reload status")}
            </p>
          </div>
          <Switch
            checked={isEnabled}
            onChange={handleToggle}
            disabled={!canManageBilling}
            aria-label={t("PAYMENT_SETTINGS.RELOAD.TOGGLE_LABEL", "Toggle auto-reload")}
          />
        </div>

        {!canManageBilling && (
          <p className="text-xs text-muted-foreground">
            {t("PAYMENT_SETTINGS.RELOAD.NO_PERMISSION", NO_PERMISSION_MSG)}
          </p>
        )}

        {isEnabled && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="auto-reload-amount" className="text-sm font-medium">
                {t("PAYMENT_SETTINGS.RELOAD.RELOAD_AMOUNT")} ({unit})
              </label>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Reload amount presets">
                {AMOUNT_PRESETS.map((preset) => (
                  <Tooltip
                    key={preset}
                    content={!canManageBilling ? NO_PERMISSION_MSG : undefined}
                  >
                    <span className="inline-flex">
                      <Button
                        type="button"
                        variant={amount === preset ? "primary" : "outline"}
                        size="sm"
                        disabled={!canManageBilling}
                        onClick={() => { setAmount(preset); setHasChanges(true); }}
                        aria-pressed={amount === preset}
                        aria-label={`Reload amount $${preset}`}
                      >
                        {`$${preset}`}
                      </Button>
                    </span>
                  </Tooltip>
                ))}
              </div>
              <Input
                id="auto-reload-amount"
                type="number"
                min={10}
                step={5}
                value={amount}
                disabled={!canManageBilling}
                onChange={(e) => {
                  if (!e.target.value.trim()) return;
                  const val = Number(e.target.value);
                  if (isNaN(val)) return;
                  setAmount(val);
                  setHasChanges(true);
                }}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="auto-reload-threshold" className="text-sm font-medium">
                {t("PAYMENT_SETTINGS.RELOAD.BALANCE_FALLS")} ({unit})
              </label>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Threshold presets">
                {THRESHOLD_PRESETS.map((preset) => (
                  <Tooltip
                    key={preset}
                    content={!canManageBilling ? NO_PERMISSION_MSG : undefined}
                  >
                    <span className="inline-flex">
                      <Button
                        type="button"
                        variant={threshold === preset ? "primary" : "outline"}
                        size="sm"
                        disabled={!canManageBilling}
                        onClick={() => { setThreshold(preset); setHasChanges(true); }}
                        aria-pressed={threshold === preset}
                        aria-label={`Threshold $${preset}`}
                      >
                        {`$${preset}`}
                      </Button>
                    </span>
                  </Tooltip>
                ))}
              </div>
              <Input
                id="auto-reload-threshold"
                type="number"
                min={1}
                step={1}
                value={threshold}
                disabled={!canManageBilling}
                onChange={(e) => {
                  if (!e.target.value.trim()) return;
                  const val = Number(e.target.value);
                  if (isNaN(val)) return;
                  setThreshold(val);
                  setHasChanges(true);
                }}
              />
              {threshold > 0 && amount > 0 && threshold >= amount && (
                <p className="text-xs text-warning" role="alert" aria-live="assertive">
                  Threshold must be less than the reload amount.
                </p>
              )}
            </div>
          </div>
        )}

        {hasChanges && canManageBilling && (
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={updateAutoReload.isPending}
            >
              {updateAutoReload.isPending ? t("ACTION.SAVING") : t("ACTION.SAVE")}
            </Button>
          </div>
        )}
    </div>
  );

  return inDialog ? body : <Card className="p-6">{body}</Card>;
}
